import React, { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import "./UniversityList.scss";
import { useNavigate } from "react-router-dom";
import {
  Row,
  Col,
  Button,
  Input,
  Table,
  Pagination,
  PaginationItem,
  PaginationLink,
  Spinner,
} from "reactstrap";
import CustomBreadCrumb from "utils/CustomBreadCrumb/CustomBreadCrumb";
import UniversityApi from "Api/UniversityApi";
import { toast } from "react-toastify";
import { ToastContainer } from "react-toastify";
import DataTableLoader from "SkeletonLoader/DataTableLoader";

const UniversityList = (props) => {
  document.title = "University ";
  const navigate = useNavigate();
  const uploadbtn = useRef();
  const [loading, setLoading] = useState(true);
  const accessToken = localStorage.getItem("accessToken");
  const userData = JSON.parse(localStorage.getItem("userData"));
  const [universityDetails, setUniversityDetails] = useState([]);
  const [filteredUniversityDetails, setFilteredUniversityDetails] = useState([]);
  const [page, setPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [limit, setLimit] = useState(10);
  const [filterStatus, setFilterStatus] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedUniversities, setSelectedUniversities] = useState([]);
  const [excelLoader, setExcelLoader] = useState(false);
  const [addUniversityLoader, setAddUniversityLoader] = useState(false);

  const breadcrumbItems = [
    { label: "Pixwik", link: "#" },
    { label: "University", link: "#" },
    { label: "University List", link: "#" },
  ];

  const getAllUniversityDetails = () => {
    const config = {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      params: {
        limit: limit,
        page: page,
      },
    };

    UniversityApi.getAllUniversity(config)
      .then((response) => {
        if (response.data.status) {
          setLoading(false);
          setUniversityDetails(response.data.university);
          setFilteredUniversityDetails(response.data.university); // Initialize filtered data
          setTotalRecords(response.data.totalRecords);
        }
      })
      .catch((error) => {
        console.error("Error fetching university:", error);
      });
  };

  useEffect(() => {
    getAllUniversityDetails();
  }, [accessToken, page, limit]);

  useEffect(() => {
    let filteredData = universityDetails;

    // Filter by status
    if (filterStatus !== "all") {
      filteredData = filteredData.filter(
        (university) => university.verifiedStatus === filterStatus,
      );
    }

    // Filter by search term
    if (searchTerm) {
      filteredData = filteredData.filter(university =>
        university.universityName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        university.universityCode?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        university.location?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredUniversityDetails(filteredData);
  }, [filterStatus, universityDetails, searchTerm]);

  const totalPages = Math.ceil(totalRecords / limit);

  const handleUploadButtonClick = () => {
    uploadbtn.current.click();
  };

  const handleFileChange = (event) => {
    setExcelLoader(true);
    const file = event.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);

    const config = {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    };

    UniversityApi.uploadUniversityByExcel(formData, config)
      .then((res) => {
        getAllUniversityDetails();
        console.log(res.data);
        event.target.value = "";
        setExcelLoader(false);
        toast.success(res.data.message);
      })
      .catch((err) => {
        console.log(err);
        event.target.value = "";
        setExcelLoader(false);
        toast.error(err.response.data.message);
      });
  };

  const handleAddUniversityClick = () => {
    setAddUniversityLoader(true);
    setTimeout(() => {
      navigate("/addUniversity");
      setAddUniversityLoader(false);
    }, 500);
  };

  const toggleSelectAll = () => {
    if (selectedUniversities.length === filteredUniversityDetails.length) {
      setSelectedUniversities([]);
    } else {
      setSelectedUniversities(filteredUniversityDetails.map((uni) => uni._id));
    }
  };

  const toggleSelection = (uniId) => {
    setSelectedUniversities((prevUni) =>
      prevUni.includes(uniId)
        ? prevUni.filter((id) => id !== uniId)
        : [...prevUni, uniId],
    );
  };

  const handleUpdateStatus = (newStatus) => {
    if (selectedUniversities.length === 0) {
      toast.warn("Please select at least one university to update.");
      return;
    }

    const config = {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    };
    const payload = {
      universityIds: selectedUniversities,
      newStatus: newStatus
    };

    UniversityApi.updateUniversityStatus(payload, config)
      .then((response) => {
        if (response.data.status) {
          toast.success(response.data.message);
          getAllUniversityDetails();
          setSelectedUniversities([]); 
        }
      })
      .catch((error) => {
        console.error("Error updating university status:", error);
        toast.error("Failed to update university status.");
      });
  };

  return (
    <>
      <ToastContainer />
      <Row>
        <Col xs="12">
          <div className="university-list-header d-flex align-items-center justify-content-between">
            <CustomBreadCrumb data={breadcrumbItems} pageTitle="University" />

            <div className="d-flex align-items-center gap-3">
              <div className="search-container">
                <Input
                  type="text"
                  placeholder="Search universities..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="search-input"
                />
                <i className="mdi mdi-magnify search-icon"></i>
              </div>
              {selectedUniversities.length === 0 && (
                <>
                  <Input
                    type="select"
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    style={{ width: "150px" }}
                  >
                    <option value="all">All</option>
                    <option value="approve">Approved</option>
                    <option value="reject">Rejected</option>
                    <option value="pending">Pending</option>
                  </Input>

                  <input
                    type="file"
                    ref={uploadbtn}
                    style={{ display: "none" }}
                    onChange={handleFileChange}
                  />
                  <Button
                    type="button"
                    color="success"
                    className="waves-effect"
                    onClick={handleUploadButtonClick}
                  >
                    {excelLoader ? (
                      <Spinner size="sm" />
                    ) : (
                      <i className="mdi mdi-microsoft-excel" />
                    )}
                  </Button>

                  <Button
                    type="button"
                    color="secondary"
                    className="waves-effect"
                    onClick={handleAddUniversityClick}
                  >
                    Add University
                  </Button>
                </>
              )}

              {selectedUniversities.length > 0 && (
                <>
                  <Button
                    type="button"
                    color="primary"
                    className="waves-effect"
                    onClick={() => handleUpdateStatus("approve")}
                  >
                    Approve
                  </Button>
                  <Button
                    type="button"
                    color="danger"
                    className="waves-effect"
                    onClick={() => handleUpdateStatus("reject")}
                  >
                    Reject
                  </Button>
                </>
              )}
            </div>
          </div>

          <div className="university-data-table table-responsive mt-4 mb-3">
            {loading ? (
              <DataTableLoader cols={10} />
            ) : (
              <Table className="table mb-0">
                <thead className="table-light">
                  <tr>
                    <th>
                      <input
                        type="checkbox"
                        onClick={toggleSelectAll}
                        checked={
                          selectedUniversities.length ===
                          filteredUniversityDetails.length
                        }
                      />
                    </th>
                    <th>#</th>
                    <th>University Name</th>
                    <th>Type</th>
                    <th>Year Established</th>
                    <th>UGC Approval</th>
                    <th>Verified Status</th>
                    <th>Edit</th>
                    <th>View</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUniversityDetails.map((university, index) => (
                    <tr key={university?._id} className="university-table-row">
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedUniversities.includes(university._id)}
                          onClick={() => {
                            toggleSelection(university._id);
                          }}
                        />
                      </td>
                      <th scope="row">{(page - 1) * limit + index + 1}</th>
                      <td>{university?.universityName}</td>
                      <td>
                        {university?.type?.charAt(0).toUpperCase() +
                          university?.type?.slice(1)}
                      </td>
                      <td>{university?.yearEstablished}</td>
                      <td>{university?.ugcApproval ? "Yes" : "No"} </td>
                      <td>
                        <span
                          className={`status ${university?.verifiedStatus.toLowerCase()}`}
                        >
                          {university?.verifiedStatus === "approve"
                            ? "Approved"
                            : university?.verifiedStatus === "reject"
                              ? "Rejected"
                              : "Pending"}
                        </span>
                      </td>

                      {university?.verifiedStatus === "pending" ||
                        (["approve", "reject"].includes(
                          university?.verifiedStatus,
                        ) &&
                          userData?.role === "manager") ? (
                        <td>
                          <span
                            className="edit-university"
                            onClick={() => {
                              navigate(
                                `/edit-university/${university?.universityCode}`,
                                { state: university },
                              );
                            }}
                          >
                            <i className="ion ion-md-create" />
                          </span>
                        </td>
                      ) : (
                        <td>
                          <span className="edit-university">
                            <i className="fas fa-ban" />
                          </span>
                        </td>
                      )}

                      <td>
                        <span
                          onClick={() => {
                            navigate(
                              `/view-university/${university?.universityCode}`,
                            );
                          }}
                          className="view-university"
                        >
                          <i className="ion ion-md-eye" />
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </div>
          <div className="pagination-container d-flex align-items-center justify-content-end">
            <Pagination>
              <PaginationItem disabled={page <= 1}>
                <PaginationLink previous onClick={() => setPage(page - 1)} />
              </PaginationItem>
              {[...Array(Math.min(3, totalPages))].map((_, index) => (
                <PaginationItem key={index} active={index + 1 === page}>
                  <PaginationLink onClick={() => setPage(index + 1)}>
                    {index + 1}
                  </PaginationLink>
                </PaginationItem>
              ))}

              
              {totalPages > 6 && page > 3 && page < totalPages - 2 && (
                <PaginationItem disabled>
                  <PaginationLink>...</PaginationLink>
                </PaginationItem>
              )}

            
              {totalPages > 6 && page > 3 && page < totalPages - 2 && (
                <PaginationItem active>
                  <PaginationLink onClick={() => setPage(page)}>
                    {page}
                  </PaginationLink>
                </PaginationItem>
              )}

             
              {totalPages > 6 && page < totalPages - 2 && (
                <PaginationItem disabled>
                  <PaginationLink>...</PaginationLink>
                </PaginationItem>
              )}

             
              {totalPages > 3 &&
                [...Array(Math.min(3, totalPages))].map((_, index) => {
                  const pageNumber = totalPages - 2 + index;
                  return (
                    <PaginationItem key={pageNumber} active={pageNumber === page}>
                      <PaginationLink onClick={() => setPage(pageNumber)}>
                        {pageNumber}
                      </PaginationLink>
                    </PaginationItem>
                  );
                })}   
              <PaginationItem disabled={page >= totalPages}>
                <PaginationLink next onClick={() => setPage(page + 1)} />
              </PaginationItem>
            </Pagination>
          </div>
        </Col>
      </Row>
    </>
  );
};

export default UniversityList;